import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Power, RefreshCw, MessageCircle, ExternalLink, AlertTriangle, Link2, Gift, CheckCircle2 } from 'lucide-react';

// 21/09 — VUE D'ENSEMBLE DU BOT. Marlon : « il y a pas mal d'infos, je suis un peu perdu pour
// certains trucs... il faut que ce soit clair et facile : OK, le bot est en train de discuter avec
// telle personne, et si je peux rapidement aller voir ». Cette vue repond a cinq questions, et rien
// d'autre : le bot est-il allume ? pour quel modele ? est-il pret ? avec qui parle-t-il ? ou en est
// chaque fan ? — avec un bouton pour ouvrir la conversation dans la messagerie.

function depuis(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

const heure = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

// Une pastille d'etat, lisible d'un coup d'oeil (21/09 : 5 infos serrees sur une ligne, illisible).
// 30/09 — POURQUOI CE FAN N'A PAS PAYE. Le bot calcule le code (`pourquoi.code`), ici on ne fait
// que l'habiller. Chaque libelle dit quoi FAIRE, pas seulement ce qui s'est passe.
const RAISONS = {
  refus: { court: 'refusent', long: 'refuse de payer, par principe', c: 'bg-rose-500/15 text-rose-200' },
  date: { court: 'ont donné une date', long: 'pas maintenant — il a donné une date', c: 'bg-sky-500/15 text-sky-200' },
  sans_sous: { court: 'sans argent', long: "dit ne pas avoir d'argent", c: 'bg-amber-500/15 text-amber-200' },
  jamais_ouvert: { court: "n'ont jamais ouvert", long: "n'a jamais ouvert le lien", c: 'bg-orange-500/15 text-orange-200' },
  ouvert_pas_paye: { court: 'ont ouvert sans payer', long: "a ouvert le lien et n'a pas payé", c: 'bg-fuchsia-500/15 text-fuchsia-200' },
  coupe: { court: 'bot coupé', long: 'le bot est coupé pour lui', c: 'bg-rose-500/15 text-rose-200' },
  pause: { court: 'en pause', long: 'le bot est en pause', c: 'bg-white/5 text-slate-400' },
};

function Pastille({ couleur, children }) {
  const c = {
    vert: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30',
    orange: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
    bleu: 'bg-sky-500/15 text-sky-200 border-sky-500/30',
    gris: 'bg-white/5 text-slate-300 border-white/10',
    rouge: 'bg-orange-500/15 text-orange-200 border-orange-500/30',
  }[couleur];
  return <span className={`text-[12px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${c}`}>{children}</span>;
}

// 30/09 (Marlon : « tire la photo du profil ») — la photo Telegram du compte, deja en base.
// Si le fichier manque ou ne charge pas, on retombe sur l'initiale coloree : une carte sans image
// reste lisible, une carte avec une image cassee ne l'est pas.
const TEINTES = ['bg-pink-500/20 text-pink-200', 'bg-indigo-500/20 text-indigo-200',
  'bg-emerald-500/20 text-emerald-200', 'bg-amber-500/20 text-amber-200', 'bg-sky-500/20 text-sky-200'];
function Avatar({ m }) {
  const [casse, setCasse] = useState(false);
  const init = String(m.name || '?').trim().charAt(0).toUpperCase();
  const teinte = TEINTES[(String(m.id || '').charCodeAt(0) || 0) % TEINTES.length];
  if (m.photo && !casse) {
    return <img src={m.photo} alt={m.name} onError={() => setCasse(true)}
      className="w-10 h-10 rounded-full object-cover shrink-0 border border-white/10" />;
  }
  return (
    <div className={`w-10 h-10 rounded-full shrink-0 flex items-center justify-center font-semibold text-[15px] ${teinte}`}>{init}</div>
  );
}

function Interrupteur({ allume, desactive, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={desactive}
      title={desactive ? "Ce modèle n'est pas prêt — complète ce qui manque avant de l'allumer" : (allume ? 'Éteindre le bot pour ce modèle' : 'Allumer le bot pour ce modèle')}
      className="w-12 h-7 rounded-full transition-colors relative shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
      style={{ background: allume ? '#37d39a' : 'rgba(255,255,255,.14)' }}>
      <span className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all shadow" style={{ left: allume ? '26px' : '4px' }} />
    </button>
  );
}

function Etat({ m }) {
  if (!m.pret) {
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30 flex items-center gap-1">
      <AlertTriangle size={11} /> Pas prêt</span>;
  }
  return m.allume
    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">● Allumé</span>
    : <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">○ Éteint</span>;
}

export default function BotVue({ onChoisirModele }) {
  const [vue, setVue] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState('');
  const [filtre, setFiltre] = useState('tous');
  // 30/09 : les filtres de la liste. `etats` = les situations cochees (cumulables) ; par defaut on
  // n'affiche QUE ce qui demande une action, sinon les 27 fans a jour noient les 5 qui attendent.
  const [etats, setEtats] = useState(new Set(['attend', 'date', 'jamais_ouvert', 'ouvert_pas_paye', 'lien']));
  const [recherche, setRecherche] = useState('');
  const [toutVoir, setToutVoir] = useState(false);
  const nav = useNavigate();

  // 14/10 (C-505) — « Si la vente a été faite par un chatter, la vente appartient au chatter, pas au bot. »
  // Ce que le bot a VENDU = ventes signées « 🤖 Bot HERA » (ChatHub, depuis le début). `f.depense`, lu chez le
  // bot, est tout ce que le fan a dépensé (ventes des chatteuses comprises) : il ne sert plus que pour ça.
  const [venduBot, setVenduBot] = useState(null);
  const charger = useCallback(() => {
    api.get('/bot-vue').then((r) => { setVue(r.data); setErreur(''); })
      .catch((e) => setErreur(e?.response?.data?.detail || 'Vue indisponible (connexion au bot).'));
    api.get('/hera/stats', { params: { days: 0 } }).then((r) => setVenduBot(r.data)).catch(() => setVenduBot(null));
  }, []);
  useEffect(() => { charger(); const t = setInterval(charger, 30000); return () => clearInterval(t); }, [charger]);

  const basculer = async (m) => {
    const cible = !m.allume;
    const n = (m.fans || []).length;
    // Liste de test VIDE = le bot repond a TOUS les fans du modele (c'est ce que dit le champ du panneau :
    // « Vide = tout le monde »). Ca doit se lire en gros au moment d'allumer, pas se decouvrir apres.
    const qui = m.liste_test
      ? `Il répondra aux ${m.liste_test} fan(s) de sa liste de test`
      : `⚠️ ATTENTION : sa liste de test est VIDE — il répondra à TOUS les fans de ${m.name}`;
    const apres = m.mode === 'az' ? ', et gardera la main même après un paiement (A→Z)' : ', puis passera la main à une chatteuse après la 1ʳᵉ vente';
    if (cible && !window.confirm(`Allumer le bot pour ${m.name} ?\n\n${qui}${apres}.`)) return;
    if (!cible && !window.confirm(`Éteindre le bot pour ${m.name} ?\n\nIl arrête de répondre tout de suite${n ? ` (${n} conversation(s) en cours)` : ''}. Rien n'est effacé.`)) return;
    setEnCours(m.id);
    try { await api.post(`/bot-panel/settings?model=${encodeURIComponent(m.id)}`, { botEnabled: cible }); charger(); }
    catch (e) { window.alert('⚠ ' + (e?.response?.data?.detail || e.message)); }
    finally { setEnCours(''); }
  };

  // 22/09 (Marlon) : « un bouton pour arrêter le bot sur une conversation, pour moi-même continuer ».
  const [coupeEnCours, setCoupeEnCours] = useState('');
  const basculerFan = async (m, f) => {
    const cible = !f.botActif;
    if (!cible && !window.confirm(`Couper le bot pour ${f.nom} ?\n\nIl arrête de lui répondre tout de suite : tu reprends la conversation toi-même. Rien n'est effacé, tu peux le rallumer quand tu veux.`)) return;
    setCoupeEnCours(f.tg);
    try { await api.post(`/bot-panel/fan-tg/${encodeURIComponent(f.tg)}/bot?model=${encodeURIComponent(m.id)}`, { active: cible }); charger(); }
    catch (e) { window.alert('⚠ ' + (e?.response?.data?.detail || e.message)); }
    finally { setCoupeEnCours(''); }
  };

  // 23/09 (Marlon) : « mets-moi le bouton retirer du bot ». Couper = il se tait mais reste coche dans
  // Telegram et dans sa liste ; Retirer = il sort des deux, la conversation redevient normale.
  const [retraitEnCours, setRetraitEnCours] = useState('');
  const retirerDuBot = async (m, f) => {
    if (!window.confirm(`Retirer ${f.nom} du bot ?

Il sort de la liste du bot ET des conversations cochées dans Telegram : le bot ne le verra plus du tout. Tu pourras le remettre depuis la messagerie avec « Confier au bot ».`)) return;
    setRetraitEnCours(f.tg);
    try {
      const { data } = await api.post('/bot-business/confier-au-bot', { account_id: m.account_id, telegram_user_id: f.tg, activer: false });
      if (data?.ok === false) window.alert('⚠ Telegram n’a pas confirmé le retrait — réessaie');
      charger();
    } catch (e) { window.alert('⚠ ' + (e?.response?.data?.detail || e.message)); }
    finally { setRetraitEnCours(''); }
  };

  // 30/09 (Marlon) : « quand je clique sur Ouvrir, que ca ne me fasse pas ouvrir la partie
  // messagerie, mais que ca m'ouvre directement dans la partie bot Telegram ». La conversation
  // s'affiche donc ICI, en lecture seule : on voit ce que le bot envoie et un peu d'historique.
  const [conv, setConv] = useState(null);          // { m, f }
  const [messages, setMessages] = useState(null);  // null = en cours, [] = vide
  const [convErreur, setConvErreur] = useState('');
  const [ventes, setVentes] = useState([]);

  const ouvrir = useCallback(async (m, f) => {
    if (!f.conversation_id) return;
    setConv({ m, f }); setMessages(null); setConvErreur(''); setVentes([]);
    try {
      const [msgs, ventesRep] = await Promise.all([
        api.get(`/conversations/${encodeURIComponent(f.conversation_id)}/messages`),
        // 30/09 (Marlon) : « j'ai pas vu l'endroit ou il a paye ». Un paiement n'est pas un message :
        // on va chercher les ventes de CETTE conversation pour poser un repere au bon moment du fil.
        api.get('/sales', { params: { conversation_id: f.conversation_id } }).catch(() => ({ data: [] })),
      ]);
      setMessages(Array.isArray(msgs.data) ? msgs.data.slice(-60) : []);
      setVentes(Array.isArray(ventesRep.data) ? ventesRep.data.filter((v) => v.status !== 'rejected' && v.status !== 'duplicate') : []);
    } catch (e) {
      setConvErreur(e?.response?.data?.detail || "Impossible de charger la conversation.");
      setMessages([]);
    }
  }, []);

  const fermer = useCallback(() => { setConv(null); setMessages(null); setConvErreur(''); setVentes([]); }, []);
  useEffect(() => {
    if (!conv) return undefined;
    const t = (e) => { if (e.key === 'Escape') fermer(); };
    window.addEventListener('keydown', t);
    return () => window.removeEventListener('keydown', t);
  }, [conv, fermer]);

  if (erreur) return <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4 mb-4 text-[13px] text-amber-100/90">{erreur}</div>;
  if (!vue) return <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4 text-sm text-slate-400">Chargement de la vue du bot…</div>;

  const modeles = vue.modeles || [];
  // 30/09 (Marlon) — CE QUI COMPTE, ET DANS QUEL ORDRE.
  // Une situation par fan, exclusive, pour que les compteurs des pastilles s'additionnent sans
  // doublon. L'ordre du test EST la priorite : un fan qui attend une reponse ET a un lien en
  // attente est d'abord « il attend » — c'est ca qui demande une action.
  const attend = (f) => !!(f.dernier && f.dernier.de === 'fan');
  const situationDe = (f) => {
    if (f.jamaisEcrit) return 'muet';
    if (!f.botActif) return 'coupe';
    if (attend(f)) return 'attend';
    if (f.pause) return 'pause';
    // 30/09 (Marlon : « dans le filtre, mets ceux qui ont donne une date ») — un lien non paye
    // n'est pas une situation, c'en est SEPT. On decline donc par la raison que le bot a calculee,
    // pour pouvoir filtrer « ceux qui ont donne une date » ou « ceux qui n'ont jamais ouvert ».
    if (f.lienEnAttente) return f.pourquoi && RAISONS[f.pourquoi.code] ? f.pourquoi.code : 'lien';
    if (f.depense > 0) return 'paye';
    return 'ok';
  };
  const SITUATIONS = [
    ['attend', 'Attendent une réponse', 'border-sky-400/60 bg-sky-500/20 text-sky-100'],
    ['date', 'Ont donné une date', 'border-sky-400/60 bg-sky-500/20 text-sky-100'],
    ['jamais_ouvert', "N'ont jamais ouvert le lien", 'border-orange-400/60 bg-orange-500/20 text-orange-100'],
    ['ouvert_pas_paye', 'Ont ouvert sans payer', 'border-fuchsia-400/60 bg-fuchsia-500/20 text-fuchsia-100'],
    ['sans_sous', "Disent ne pas avoir d'argent", 'border-amber-400/60 bg-amber-500/20 text-amber-100'],
    ['refus', 'Refusent de payer', 'border-rose-400/60 bg-rose-500/20 text-rose-100'],
    ['lien', 'Lien envoyé, pas payé', 'border-amber-400/60 bg-amber-500/20 text-amber-100'],
    ['paye', 'Ont payé', 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100'],
    ['pause', 'En pause', 'border-slate-400/50 bg-slate-500/20 text-slate-100'],
    ['coupe', 'Bot coupé', 'border-rose-400/60 bg-rose-500/20 text-rose-100'],
    ['muet', 'Jamais écrit', 'border-slate-400/50 bg-slate-500/20 text-slate-100'],
    ['ok', 'À jour', 'border-white/20 bg-white/10 text-slate-200'],
  ];
  const SITU_META = {
    date: { l: 'a donné une date', c: 'bg-sky-500/15 text-sky-200' },
    jamais_ouvert: { l: "jamais ouvert", c: 'bg-orange-500/15 text-orange-200' },
    ouvert_pas_paye: { l: 'ouvert, pas payé', c: 'bg-fuchsia-500/15 text-fuchsia-200' },
    sans_sous: { l: "pas d'argent", c: 'bg-amber-500/15 text-amber-200' },
    refus: { l: 'refuse', c: 'bg-rose-500/15 text-rose-200' },
    attend: { l: 'il attend', c: 'bg-sky-500/15 text-sky-200' },
    lien: { l: 'lien non payé', c: 'bg-amber-500/15 text-amber-200' },
    paye: { l: 'a payé', c: 'bg-emerald-500/15 text-emerald-200' },
    pause: { l: 'en pause', c: 'bg-white/5 text-slate-400' },
    coupe: { l: 'bot coupé', c: 'bg-rose-500/15 text-rose-200' },
    muet: { l: 'jamais écrit', c: 'bg-white/5 text-slate-400' },
    ok: { l: 'à jour', c: 'bg-white/5 text-slate-400' },
  };
  const PAR_PAGE = 8;

  // Les totaux du bandeau du haut : une seule lecture pour toute la page.
  const TOUS = modeles.flatMap((m) => (m.fans || []).map((f) => ({ m, f })));
  const TOT = {
    ontPaye: TOUS.filter(({ f }) => (f.depense || 0) > 0).sort((a2, b2) => (b2.f.depense || 0) - (a2.f.depense || 0)),
    get payeurs() { return this.ontPaye.length; },
    get encaisse() { return this.ontPaye.reduce((n, { f }) => n + (f.depense || 0), 0); },
    neufs: modeles.reduce((n, m) => n + (m.nouveaux_aujourdhui || 0), 0),
    neufs7: modeles.reduce((n, m) => n + (m.nouveaux_7j || 0), 0),
    attendent: TOUS.filter(({ f }) => f.dernier && f.dernier.de === 'fan').length,
    liens: TOUS.filter(({ f }) => f.lienEnAttente).length,
    // 30/09 (Marlon : « c'est pas que ca, y a plein de raisons pour lesquelles ils ne payent pas,
    // j'espere que tu mets les raisons juste a cote ») — la VENTILATION du chiffre. Un « 19 liens
    // pas payes » tout seul ne dit pas quoi faire ; « 9 n'ont jamais ouvert le lien » si.
    get raisons() {
      const n = {};
      for (const { f } of TOUS) {
        if (!f.pourquoi) continue;
        n[f.pourquoi.code] = (n[f.pourquoi.code] || 0) + 1;
      }
      return n;
    },
    get raisonCourte() {
      const r = this.raisons;
      const tri = Object.entries(r).sort((a2, b2) => b2[1] - a2[1]);
      if (!tri.length) return this.liens ? 'raison inconnue' : '';
      return tri.slice(0, 2).map(([c, v]) => `${v} ${RAISONS[c] ? RAISONS[c].court : c}`).join(' · ');
    },
  };

  // Tous les fans du modele choisi, avec le nombre de ses messages non repondus (`enAttenteDepuis`,
  // pose par le bot) — c'est ce « x5 » qui montre d'un coup d'oeil qu'il parle dans le vide.
  const duModele = modeles.flatMap((m) => (m.fans || []).map((f) => ({ m, f })))
    .filter(({ m }) => (filtre === 'tous' || m.id === filtre));
  const q = recherche.trim().toLowerCase();
  const filtrees = duModele
    .filter(({ f }) => (!etats.size || etats.has(situationDe(f))))
    .filter(({ f }) => (!q || String(f.nom || '').toLowerCase().includes(q)))
    // Celui qui attend depuis le plus longtemps d'abord : c'est lui qui souffre le plus.
    .sort((x, y) => {
      const ax = attend(x.f), ay = attend(y.f);
      if (ax !== ay) return ax ? -1 : 1;
      const dx = String(x.f.dernierMsg || ''), dy = String(y.f.dernierMsg || '');
      return ax ? dx.localeCompare(dy) : dy.localeCompare(dx);
    });
  const visibles = toutVoir ? filtrees : filtrees.slice(0, PAR_PAGE);
  const basculerEtat = (cle) => setEtats((s) => {
    const n = new Set(s);
    if (n.has(cle)) n.delete(cle); else n.add(cle);
    return n;
  });

  return (
    <div data-testid="bot-vue" className="mb-6 space-y-4">
      {/* 30/09 : la conversation s'ouvre ICI, pas dans la messagerie. Lecture seule. */}
      {conv && (
        <div data-testid="bot-vue-conversation" className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,.65)' }} onClick={fermer}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-white/10 bg-[#0C0C0F]">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
              <div className="min-w-0">
                <div className="text-white font-bold text-[15px] truncate">{conv.f.nom}</div>
                <div className="text-[12px] text-slate-400">
                  {conv.m.name}
                  {conv.f.depense > 0 && <span className="text-emerald-300"> · a payé {conv.f.depense} €</span>}
                  {conv.f.lienEnAttente && <span className="text-amber-300"> · lien envoyé, pas payé</span>}
                  {conv.f.pourquoi && (
                    <span className="text-slate-300"> · {(RAISONS[conv.f.pourquoi.code] || {}).long || conv.f.pourquoi.texte}
                      {conv.f.pourquoi.mots && <span className="text-slate-400"> — « {conv.f.pourquoi.mots} »</span>}
                    </span>
                  )}
                  {!conv.f.botActif && <span className="text-rose-300"> · bot coupé</span>}
                </div>
              </div>
              {conv.f.lienEnAttente && (
                <button data-testid="bot-vue-aller-au-lien" onClick={() => {
                  const el = document.getElementById('bot-vue-lien');
                  if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
                }}
                  title="Aller au message qui porte le lien de paiement"
                  className="rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 px-3 py-1.5 text-xs text-amber-200 shrink-0 mr-2">
                  Aller au lien
                </button>
              )}
              <button data-testid="bot-vue-fermer" onClick={fermer}
                className="rounded-lg border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1.5 text-xs text-slate-300 shrink-0">
                Fermer
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {messages === null && <div className="text-sm text-slate-500">Chargement de la conversation…</div>}
              {convErreur && <div className="text-sm text-amber-300">{convErreur}</div>}
              {messages && messages.length === 0 && !convErreur && (
                <div className="text-sm text-slate-500">Aucun message.</div>
              )}
              {(() => {
                // messages + paiements, ranges dans l'ordre du temps
                const fil = [
                  ...(messages || []).map((msg) => ({ t: msg.created_at, type: 'msg', msg })),
                  ...ventes.map((v) => ({ t: v.date, type: 'vente', v })),
                ].sort((x, y) => String(x.t || '').localeCompare(String(y.t || '')));
                return fil.map((e, i) => e.type === 'vente' ? (
                  <div key={'v' + i} data-testid="bot-vue-paiement"
                    className="my-1 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.12] px-3 py-1.5">
                    <span className="text-emerald-200 text-[13px] font-semibold">
                      💶 IL A PAYÉ {Math.round(e.v.amount)} {e.v.currency === 'USD' ? '$' : '€'}
                    </span>
                    <span className="text-[11px] text-emerald-300/70 ml-2">
                      {heure(e.v.date)}{e.v.type === 'donation' ? ' · don' : ''}{e.v.source === 'auto' ? ' · encaissé automatiquement' : ''}
                    </span>
                  </div>
                ) : (() => {
                const msg = e.msg;
                const deLui = msg.from_ === 'them';
                const humain = !deLui && !!msg.chatter_id;
                const qui = deLui ? 'Lui' : humain ? (msg.chatter_name || 'Une chatteuse') : 'Le bot';
                const couleur = deLui ? 'text-sky-300' : humain ? 'text-amber-300' : 'text-emerald-300';
                // 30/09 (Marlon : « il faudrait que tu surlignes rapidement ou est-ce que le lien a
                // ete envoye, j'ai du mal a voir ») — LE MESSAGE QUI PORTE LE LIEN EST SURLIGNE.
                // Un lien de paiement se reconnait a son adresse Dropp, ou au drapeau que ChatHub
                // pose dessus (`has_dropfans_link`). On l'encadre en ambre, on l'annonce, et on lui
                // donne une ancre pour pouvoir y sauter d'un bouton.
                const porteLien = !deLui && (msg.has_dropfans_link
                  || /dropp\.fans|link_[A-Za-z0-9_-]{6}/i.test(String(msg.text || '')));
                return (
                  <div key={msg.id || i} id={porteLien ? 'bot-vue-lien' : undefined}
                    data-testid={porteLien ? 'bot-vue-message-lien' : undefined}
                    className={`${deLui ? '' : 'pl-6'} ${porteLien ? 'rounded-lg border border-amber-500/50 bg-amber-500/[0.10] px-2 py-1.5 my-1' : ''}`}>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-[11px] font-semibold ${couleur}`}>{qui}</span>
                      <span className="text-[10px] text-slate-600">{heure(msg.created_at)}</span>
                      {porteLien && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200">le lien de paiement</span>}
                    </div>
                    <div className="text-[13.5px] text-slate-200 whitespace-pre-wrap break-words">
                      {msg.text || <span className="text-slate-500 italic">(média)</span>}
                    </div>
                  </div>
                );
                })());
              })()}
            </div>
            <div className="p-3 border-t border-white/10 text-[11px] text-slate-500">
              Lecture seule — pour écrire, ouvre la messagerie. Les {(messages || []).length} derniers messages.
            </div>
          </div>
        </div>
      )}
      {/* 30/09 (Marlon) — « pour les modèles, en liste c'est un peu difficile à voir. Peut-être sous
          forme de carré avec la photo du modèle ». Une CARTE par modèle : sa photo Telegram (déjà en
          base, aspirée par la synchro), son nom, l'interrupteur SUR la carte, et ses trois chiffres.
          Ce qui était un réglage (« ouvert à tous », liste de test, scénarios cochés) descend dans le
          bloc de réglages : ici on ne garde que ce qu'on lit d'un coup d'œil. */}
      {/* 30/09 (Marlon : « pourquoi "ce que le bot rapporte" est en bas après tes modèles ? ça doit
          être fluide ») — CE BLOC PASSE EN PREMIER. Avant, il répétait les mêmes chiffres modèle par
          modèle, tout en bas. Maintenant c'est UN total en haut de page, et le détail par modèle vit
          sur sa carte juste en dessous : on lit une fois, pas deux. */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h2 className="text-lg font-bold text-white mb-3">Ce que le bot rapporte</h2>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
          {[
            ['vendu par le bot', venduBot ? `${(venduBot.ca_generated || 0).toLocaleString('fr-FR', { minimumFractionDigits: (venduBot.ca_generated || 0) % 1 ? 2 : 0, maximumFractionDigits: 2 })} €` : '…',
              venduBot ? `${venduBot.nb_ventes || 0} vente${(venduBot.nb_ventes || 0) > 1 ? 's' : ''} signée${(venduBot.nb_ventes || 0) > 1 ? 's' : ''} 🤖 Bot HERA, depuis le début` : '',
              venduBot && venduBot.ca_generated ? 'text-emerald-300' : 'text-slate-400'],
            ['nouveaux aujourd’hui', TOT.neufs, TOT.neufs7 > TOT.neufs ? `${TOT.neufs7} sur 7 jours` : '', 'text-sky-300'],
            ['attendent une réponse', TOT.attendent, TOT.attendent ? 'le bot les sert par vagues' : 'tout est à jour', TOT.attendent ? 'text-amber-200' : 'text-slate-400'],
            ['liens pas encore payés', TOT.liens, TOT.raisonCourte, TOT.liens ? 'text-amber-200' : 'text-slate-400'],
          ].map(([libelle, valeur, sous, couleur]) => (
            <div key={libelle} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="text-[11px] text-slate-400">{libelle}</div>
              <div className={`text-[22px] font-bold mt-0.5 ${couleur}`}>{valeur}</div>
              {sous && <div className="text-[11px] text-slate-500">{sous}</div>}
            </div>
          ))}
        </div>
        {TOT.ontPaye.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/5 text-[12px] text-slate-400">
            Ont payé grâce au bot :{' '}
            {TOT.ontPaye.map(({ m, f }, i) => (
              <button key={m.id + f.tg} onClick={() => ouvrir(m, f)}
                className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2">
                {i ? ' · ' : ''}{f.nom} ({(f.depense || 0).toLocaleString('fr-FR', { minimumFractionDigits: (f.depense || 0) % 1 ? 2 : 0, maximumFractionDigits: 2 })} €)
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-white">Tes modèles</h2>
          <button onClick={charger} className="text-slate-400 hover:text-white flex items-center gap-1 text-xs" title="Rafraîchir">
            <RefreshCw size={13} /> actualiser
          </button>
        </div>
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
          {modeles.map((m) => {
            const fans = m.fans || [];
            const ontPaye = fans.filter((f) => (f.depense || 0) > 0);
            const encaisse = ontPaye.reduce((n, f) => n + (f.depense || 0), 0);
            const attendent = fans.filter((f) => f.dernier && f.dernier.de === 'fan').length;
            return (
              <div key={m.id} data-testid={`bot-vue-modele-${m.id}`}
                className={`rounded-xl border p-3 ${m.allume ? 'border-white/12 bg-white/[0.03]' : 'border-white/[0.07] bg-white/[0.015] opacity-70'}`}>
                <div className="flex items-center gap-2.5 mb-2.5">
                  <Avatar m={m} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold text-[15px] truncate">{m.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">
                      {!m.pret ? <span className="text-amber-200">manque : {m.manque.join(' · ')}</span>
                        : m.allume ? (m.mode === 'az' ? 'gère tout de A à Z' : 'accueil + 1ʳᵉ vente')
                          : 'éteint — rien ne part'}
                    </div>
                  </div>
                  <Interrupteur testid={`bot-vue-interrupteur-${m.id}`} allume={m.allume}
                    desactive={!m.pret || enCours === m.id} onClick={() => basculer(m)} />
                </div>
                {fans.length > 0 && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                    <span><b className="text-white text-[15px]">{fans.length}</b><br />suivis</span>
                    {m.nouveaux_aujourdhui > 0 && (
                      <span data-testid={`bot-vue-neufs-${m.id}`}><b className="text-sky-300 text-[15px]">{m.nouveaux_aujourdhui}</b><br />nouveaux</span>
                    )}
                    {attendent > 0 && <span><b className="text-amber-200 text-[15px]">{attendent}</b><br />attendent</span>}
                    <span><b className={`text-[15px] ${encaisse ? 'text-emerald-300' : 'text-slate-500'}`}>
                      {encaisse.toLocaleString('fr-FR', { minimumFractionDigits: encaisse % 1 ? 2 : 0, maximumFractionDigits: 2 })} €</b><br /><span title="Tout ce que ces fans ont payé, ventes des chatteuses comprises (pas seulement le bot)">dépensé par ces fans</span></span>
                  </div>
                )}
                {/* 02/10 (Marlon : « sur l'appli ça me met Chloé à 15 suivis, est-ce que c'est normal ? »)
                    D'OÙ VIENT LE NOMBRE. Il valait 15 quand le bot servait 57 fans : la vue ne comptait
                    que les fans nommés et ignorait tous ceux qu'il sert par leur date d'arrivée. C'est
                    corrigé côté bot ; ici on montre la décomposition, pour qu'un chiffre qui bouge soit
                    explicable au lieu d'être à croire. */}
                {m.perimetre && (
                  <div data-testid={`bot-vue-perimetre-${m.id}`} className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
                    {m.perimetre.liste_test > 0
                      ? <>limité à sa <b className="text-slate-300">liste de test</b> ({m.perimetre.liste_test} fan{m.perimetre.liste_test > 1 ? 's' : ''})</>
                      : <>
                          <b className="text-slate-300">{m.perimetre.confies}</b> confié{m.perimetre.confies > 1 ? 's' : ''} à la main
                          {m.perimetre.depuis && <> · <b className="text-slate-300">{m.perimetre.nouveaux_depuis_bascule}</b> arrivé{m.perimetre.nouveaux_depuis_bascule > 1 ? 's' : ''} depuis le {new Date(m.perimetre.depuis).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</>}
                        </>}
                    {m.perimetre.exclus > 0 && <> · <b className="text-rose-300">{m.perimetre.exclus}</b> retiré{m.perimetre.exclus > 1 ? 's' : ''} du bot</>}
                    {/* 02/10 : l'écart entre ce que le bot pourrait prendre et ce qu'il sert
                        vraiment. Il était calculé mais jamais affiché : un chiffre qu'on ne peut
                        pas recouper est un chiffre qu'il faut croire. */}
                    {m.perimetre.eligibles > m.perimetre.total && (
                      <span className="block text-slate-600">
                        {m.perimetre.total} servi{m.perimetre.total > 1 ? 's' : ''} sur {m.perimetre.eligibles} que le bot pourrait prendre
                      </span>
                    )}
                    {m.perimetre.sature && <span className="block text-amber-200/90">⚠ la base a rendu son maximum de fiches : ce total est un plancher, pas un total</span>}
                  </div>
                )}
                {/* 30/09 : l'alerte qui compte vraiment. Mesure du jour : 0 étape décrite sur les
                    3 modèles, alors que le bot LIT ces descriptions pour savoir si ce qu'un fan
                    réclame est dans le prochain contenu payant. Sans elles, il vend à l'aveugle. */}
                {m.script && m.script.payants > 0 && m.script.decrits < m.script.contenus && (
                  <button data-testid={`bot-vue-decrits-${m.id}`} onClick={() => onChoisirModele && onChoisirModele(m.id)}
                    className="mt-2.5 w-full text-left rounded-lg border border-amber-500/40 bg-amber-500/[0.08] px-2.5 py-1.5 text-[11px] text-amber-100 hover:bg-amber-500/[0.14]">
                    ⚠ {m.script.decrits}/{m.script.contenus} étapes décrites, tous funnels confondus — le bot vend sans savoir ce qu&rsquo;il y a dans les médias. Les décrire ↓
                  </button>
                )}
                <button onClick={() => onChoisirModele && onChoisirModele(m.id)}
                  className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1.5 text-[11px] text-slate-200">
                  Régler {m.name} ↓
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* 30/09 (Marlon) — REFONTE DE CETTE SECTION. Avant : une carte de ~90 px par fan, tous les
          fans affiches, aucun filtre. Avec 57 fans sur Chloe ca faisait ~5 000 px a defiler, et les
          27 qui n'attendaient rien noyaient les 5 qui demandaient une action. Maintenant : une LIGNE
          par fan (36 px), un filtre par etat avec son compteur, une recherche, et 8 lignes par
          defaut. Le voyant de gauche dit si le bot PARLE a ce fan en ce moment. */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><MessageCircle size={17} className="text-indigo-400" /> Ce que le bot fait en ce moment</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Chercher un nom"
              className="rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs text-white placeholder-slate-500 w-[150px] focus:outline-none focus:border-indigo-400/60" />
            <div className="flex items-center gap-1 rounded-lg border border-white/10 p-0.5">
              {[['tous', 'Tous'], ...modeles.filter((m) => (m.fans || []).length).map((m) => [m.id, m.name])].map(([id, l]) => (
                <button key={id} onClick={() => setFiltre(id)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${filtre === id ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {SITUATIONS.map(([cle, libelle, classe]) => {
            const n = duModele.filter(({ f }) => situationDe(f) === cle).length;
            if (!n && !etats.has(cle)) return null;
            const on = etats.has(cle);
            return (
              <button key={cle} data-testid={`bot-vue-filtre-${cle}`} onClick={() => basculerEtat(cle)}
                className={`px-2.5 py-1 text-[12px] rounded-full border transition-colors ${on ? classe : 'border-white/10 bg-white/[0.03] text-slate-400 hover:text-white'}`}>
                {libelle} <b>{n}</b>
              </button>
            );
          })}
        </div>

        {!duModele.length && <div className="text-sm text-slate-500 py-3">Aucune conversation pour l'instant.</div>}

        {duModele.length > 0 && (
          <div className="rounded-xl border border-white/10 overflow-hidden">
            {visibles.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-400">Personne dans ces états — tout est à jour.</div>
            ) : visibles.map(({ m, f }, i) => {
              const sit = situationDe(f);
              const meta = SITU_META[sit] || SITU_META.ok;
              const parle = f.botActif && !f.pause && m.allume;
              const voyant = !m.allume ? 'bg-slate-600' : !f.botActif ? 'bg-rose-500' : f.pause ? 'bg-amber-400' : 'bg-emerald-400';
              const titreVoyant = !m.allume ? `Le bot est éteint sur ${m.name}`
                : !f.botActif ? 'Bot coupé pour ce fan — repris à la main'
                  : f.pause ? `En pause jusqu'à ${heure(f.pause.jusqua)}${f.pause.raison === 'nuit' ? ' (nuit)' : ''}`
                    : 'Le bot lui parle en ce moment';
              return (
                <div key={m.id + f.tg} data-testid={`bot-vue-conv-${f.tg}`}
                  className={`flex items-center gap-2.5 px-3 py-2 ${i ? 'border-t border-white/5' : ''} hover:bg-white/[0.02]`}>
                  <span title={titreVoyant} aria-label={titreVoyant}
                    className={`w-2 h-2 rounded-full shrink-0 ${voyant} ${parle ? 'ring-2 ring-emerald-400/25' : ''}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] truncate">
                      <span className="text-white">{f.nom}</span>
                      {filtre === 'tous' && <span className="text-slate-500"> · {m.name}</span>}
                      {f.enAttenteDepuis > 1 && <b className="text-sky-300"> ×{f.enAttenteDepuis}</b>}
                      <span className="text-slate-500"> · </span>
                      <span className="text-slate-400">{f.dernier ? `${f.dernier.de === 'fan' ? 'lui' : 'bot'} : ${f.dernier.texte}` : (f.jamaisEcrit ? "n'a jamais écrit" : '—')}</span>
                    </div>
                    {/* 02/10 (Marlon) : « le plus important ce n'est pas s'il l'a ouvert ou pas,
                        c'est SA REPONSE quand on lui envoie le lien. C'est ca qu'on a besoin de
                        voir. » Un fan qui repond « j'ai pas les moyens », un qui repond « je vois
                        rien » et un qui repond « c'est cher » n'ont rien a voir — et « ouvert /
                        pas ouvert » ne les distinguait pas. */}
                    {f.reponseAuLien && (
                      <div data-testid={`bot-vue-reponse-lien-${f.tg}`} className="text-[11px] truncate mt-0.5">
                        <span className="text-fuchsia-300/90">lien{f.reponseAuLien.heures != null ? ` il y a ${f.reponseAuLien.heures} h` : ''}</span>
                        <span className="text-slate-600"> → </span>
                        {(f.reponseAuLien.reponses || []).length
                          ? <span className="text-slate-300" title={(f.reponseAuLien.reponses || []).join(String.fromCharCode(10))}>
                              &laquo;&nbsp;{(f.reponseAuLien.reponses || []).join(' · ')}&nbsp;&raquo;
                            </span>
                          : <span className="text-amber-200/80">il n&rsquo;a rien répondu</span>}
                      </div>
                    )}
                  </div>
                  {/* 30/09 : LA RAISON, juste a cote du statut. Le bot la calcule (`pourquoi`), on
                      l'habille. Quand le fan a donne une date dans ses propres mots, on met SES mots
                      en infobulle : c'est la que se trouve l'information utile, pas dans un code. */}
                  {f.pourquoi ? (
                    <span data-testid={`bot-vue-pourquoi-${f.tg}`}
                      title={[RAISONS[f.pourquoi.code] ? RAISONS[f.pourquoi.code].long : f.pourquoi.texte,
                        f.pourquoi.mots ? `Ses mots : « ${f.pourquoi.mots} »` : '',
                        f.pourquoi.quand ? `Date annoncée : ${new Date(f.pourquoi.quand).toLocaleDateString('fr-FR')}` : '',
                        f.pourquoi.jours != null ? `Lien envoyé il y a ${f.pourquoi.jours} jour(s)` : '',
                        f.pourquoi.ouvert ? `Ouvert ${f.pourquoi.ouvert.fois} fois` : ''].filter(Boolean).join(String.fromCharCode(10))}
                      className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${(RAISONS[f.pourquoi.code] || {}).c || 'bg-white/5 text-slate-400'}`}>
                      {(RAISONS[f.pourquoi.code] || {}).court || f.pourquoi.code}
                      {f.pourquoi.quand && <span className="opacity-70"> {new Date(f.pourquoi.quand).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
                    </span>
                  ) : (
                  <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${meta.c}`}>{meta.l}</span>
                  )}
                  <span className="text-[11px] text-slate-500 w-[56px] text-right shrink-0 tabular-nums">{depuis(f.dernierMsg || (f.dernier && f.dernier.quand))}</span>
                  <span className="text-[11px] text-slate-400 w-[38px] text-right shrink-0 tabular-nums" title={`${(f.etape.scenario || '').replace(/^\u{1F4DC}\s*/u, '')} — prochain : ${f.etape.prochaine}`}>
                    {f.etape.faite}/{f.etape.total}
                  </span>
                  <span className={`text-[11px] w-[52px] text-right shrink-0 tabular-nums ${f.depense > 0 ? 'text-emerald-300' : 'text-slate-600'}`}>
                    {f.depense > 0 ? `${f.depense.toLocaleString('fr-FR', { minimumFractionDigits: f.depense % 1 ? 2 : 0, maximumFractionDigits: 2 })} €` : '—'}
                  </span>
                  {!f.botActif && (
                    <button data-testid={`bot-vue-couper-${f.tg}`} onClick={() => basculerFan(m, f)} disabled={coupeEnCours === f.tg}
                      title="Rendre la conversation au bot"
                      className="rounded border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 px-2 py-0.5 text-[11px] text-emerald-200 shrink-0 disabled:opacity-40">
                      Rallumer
                    </button>
                  )}
                  <button data-testid={`bot-vue-retirer-${f.tg}`} onClick={() => retirerDuBot(m, f)} disabled={retraitEnCours === f.tg || !m.account_id}
                    title="Retirer ce fan du bot"
                    className="rounded border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-2 py-0.5 text-[11px] text-slate-300 shrink-0 disabled:opacity-40">
                    Retirer
                  </button>
                  <button data-testid={`bot-vue-ouvrir-${f.tg}`} onClick={() => ouvrir(m, f)} disabled={!f.conversation_id}
                    title={f.conversation_id ? 'Voir la conversation ici' : 'Conversation introuvable'}
                    className="rounded border border-indigo-500/40 bg-indigo-500/10 hover:bg-indigo-500/20 px-2 py-0.5 text-[11px] text-indigo-200 shrink-0 disabled:opacity-40">
                    Ouvrir
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {duModele.length > 0 && (
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <span className="text-[11px] text-slate-500">
              {visibles.length} affiché{visibles.length > 1 ? 's' : ''} sur {filtrees.length}
              {filtrees.length !== duModele.length && <> — {duModele.length} au total</>}
              <span className="text-slate-600"> · </span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> le bot lui parle</span>
              <span className="text-slate-600"> · </span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> en pause</span>
              <span className="text-slate-600"> · </span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block" /> coupé</span>
            </span>
            {filtrees.length > PAR_PAGE && (
              <button data-testid="bot-vue-plus" onClick={() => setToutVoir((v) => !v)}
                className="rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] px-3 py-1 text-[12px] text-slate-300">
                {toutVoir ? 'Réduire' : `Voir ${filtrees.length - PAR_PAGE} de plus`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
